import type { Message as HonchoMessage, SessionContext } from '@honcho-ai/sdk'
import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from '@/lib/api/client'
import { loadGuestSession, saveGuestSession } from '@/lib/guestStorage'
import { generateUUID } from '@/lib/utils'
import { ASSISTANT_ID } from '@shared/constants/honcho'
import type {
  ChatMessage,
  ChatMessageWithTimestamp,
} from '@/types/chat/ChatMessage'
import type { GuestSession } from '@/types/GuestSession'
import { getSelectedIdentity, saveSelectedIdentity } from '@/types/UserSettings'
import { useUser } from '../useUser'

export interface Session {
  id: string
  messages: ChatMessageWithTimestamp[]
  created_at: Date
  updated_at: Date
  message_count: number
  interview_mode: boolean
}

export interface SessionMetadata {
  created_at: string
  updated_at: string
  message_count: number
  interview_mode?: boolean
}

interface UseSessionReturn {
  currentSession: Session
  isLoadingMessages: boolean
  hasMoreMessages: boolean
  isLoadingMoreMessages: boolean
  hasMorePreviousMessages: boolean
  isLoadingPreviousMessages: boolean
  isGuestMode: boolean
  loadSessions: () => Promise<void>
  switchSession: (sessionId: string) => Promise<void>
  saveMessages: (
    newMessages: ChatMessage[],
    sessionId?: string
  ) => Promise<void>
  renderMessages: (messages: ChatMessageWithTimestamp[]) => Promise<void>
  updateCurrentMessage: (message: ChatMessageWithTimestamp) => Promise<void>
  loadMoreMessages: () => Promise<void>
  loadPreviousMessages: () => Promise<void>
  migrateGuestSession: (guestMessages: ChatMessage[]) => Promise<void>
}

interface PaginationState {
  startIndex: number
  endIndex: number
  totalMessages: number
}

interface PaginatedMessagesResponse {
  messages: HonchoMessage[]
  startIndex?: number
  endIndex?: number
  totalMessages?: number
  hasMore?: boolean
}

type SessionMetadataResponse = SessionMetadata | undefined

const MESSAGE_PAGE_SIZE = 50

/**
 * Creates an in-memory session placeholder before backend data arrives, ensuring timestamps and counts are initialized.
 */
function createSession(id: string, interviewMode = false): Session {
  const now = new Date()
  return {
    id,
    messages: [],
    created_at: now,
    updated_at: now,
    message_count: 0,
    interview_mode: interviewMode,
  }
}

/**
 * Creates a guest session placeholder (no backend persistence)
 */
function createGuestSession(): Session {
  const now = new Date()
  return {
    id: 'guest-session',
    messages: [],
    created_at: now,
    updated_at: now,
    message_count: 0,
    interview_mode: false,
  }
}

/**
 * Fetches a truncated session context suitable for quickly resuming a conversation without loading the full history.
 */
async function fetchSessionContext(sessionId: string, signal?: AbortSignal) {
  const { data, error } = await api.api.chat['get-context'].post({ sessionId }, { fetch: { signal } })
  if (error) {
    throw new Error(`Failed to fetch context: ${error}`)
  }
  return data
}

/**
 * Retrieves a paginated slice of full session messages, enabling targeted pagination in either direction.
 */
async function fetchFullSessionMessages(
  sessionId: string,
  targetMessageId?: string,
  offset?: number,
  limit: number = MESSAGE_PAGE_SIZE,
  signal?: AbortSignal
): Promise<PaginatedMessagesResponse> {
  const params: {
    sessionId: string
    limit: string
    targetMessageId?: string
    offset?: string
  } = {
    sessionId,
    limit: limit.toString(),
  }
  if (targetMessageId) {
    params.targetMessageId = targetMessageId
  }
  if (typeof offset === 'number') {
    params.offset = offset.toString()
  }
  const { data, error } = await api.api.chat['get-session-messages'].get({
      query: params,
      fetch: { signal },
    })
  if (error) {
    throw new Error(`Failed to fetch full messages: ${error}`)
  }
  return {
    messages: Array.isArray(data?.messages)
      ? (data.messages as HonchoMessage[])
      : [],
    startIndex: data?.startIndex,
    endIndex: data?.endIndex,
    totalMessages: data?.totalMessages,
    hasMore: data?.hasMore,
  }
}

/**
 * Converts raw Honcho messages into the chat message shape consumed by the UI while preserving timestamps and IDs.
 * Filters out tool messages (they're internal to conversation flow, not displayed in UI).
 */
function normalizeHonchoMessages(
  rawMessages: HonchoMessage[] | undefined
): ChatMessageWithTimestamp[] {
  if (!Array.isArray(rawMessages)) {
    return []
  }
  return rawMessages
    .filter((message) => {
      // Filter out tool messages - they're internal to conversation flow
      return message?.metadata?.messageType !== 'tool'
    })
    .map((message) => {
      const role: 'user' | 'assistant' =
        message?.peerId === ASSISTANT_ID ? 'assistant' : 'user'
      const content = typeof message?.content === 'string' ? message.content : ''
      const timestamp = new Date(message?.createdAt ?? new Date())
      const id = typeof message?.id === 'string' ? message.id : undefined
      
      // Extract tool calls from metadata for display if present
      const metadata: any = {}
      if (message?.metadata?.tool_calls) {
        metadata.tool_calls = message.metadata.tool_calls
      }
      // Preserve other metadata like replyTo, toolInvocations (for backward compatibility)
      if (message?.metadata) {
        Object.assign(metadata, message.metadata)
      }
      
      return {
        role,
        content,
        timestamp,
        id,
        ...(Object.keys(metadata).length > 0 && { metadata }),
      }
    })
}

/**
 * Produces a session object by combining backend metadata with normalized messages.
 */
function createSessionFromMetadata(
  sessionId: string,
  metadata: SessionMetadataResponse,
  messages: ChatMessageWithTimestamp[]
): Session {
  const createdAt = metadata?.created_at
    ? new Date(metadata.created_at)
    : new Date()
  const updatedAt = metadata?.updated_at
    ? new Date(metadata.updated_at)
    : new Date()
  const messageCount =
    typeof metadata?.message_count === 'number'
      ? metadata.message_count
      : messages.length
  const interviewMode = metadata?.interview_mode === true
  return {
    id: sessionId,
    messages,
    created_at: createdAt,
    updated_at: updatedAt,
    message_count: messageCount,
    interview_mode: interviewMode,
  }
}

/**
 * Maps a session context response to a session object ready for rendering, handling truncated histories.
 */
function buildSessionFromContext(
  sessionId: string,
  context: SessionContext | undefined,
  metadata: SessionMetadataResponse
): Session {
  const messages = normalizeHonchoMessages(
    context?.messages as HonchoMessage[] | undefined
  )
  return createSessionFromMetadata(sessionId, metadata, messages)
}

/**
 * Maps a paginated messages response into a full session plus pagination metadata flags for the UI.
 */
function _buildSessionFromMessages(
  sessionId: string,
  messagesData: PaginatedMessagesResponse,
  metadata: SessionMetadataResponse
) {
  const messages = normalizeHonchoMessages(messagesData.messages)
  const session = createSessionFromMetadata(sessionId, metadata, messages)
  const pagination: PaginationState = {
    startIndex: messagesData.startIndex ?? 0,
    endIndex: messagesData.endIndex ?? messages.length,
    totalMessages: messagesData.totalMessages ?? messages.length,
  }
  return {
    session,
    pagination,
    hasMore: Boolean(messagesData.hasMore),
    hasPrevious: pagination.startIndex > 0,
  }
}

/**
 * Derives pagination state when only a total count and the number of loaded messages are known.
 */
function derivePaginationFromCounts(totalCount: number, loadedCount: number) {
  const safeTotal = Math.max(totalCount, loadedCount)
  const startIndex = Math.max(0, safeTotal - loadedCount)
  const pagination: PaginationState = {
    startIndex,
    endIndex: safeTotal,
    totalMessages: safeTotal,
  }
  return {
    pagination,
    hasPrevious: startIndex > 0,
  }
}

export function useSession(): UseSessionReturn {
  const savedInitialSessionId = getSelectedIdentity()
  const { user, isLoading: isAuthLoading } = useUser()

  const [currentSession, setCurrentSession] = useState<Session>(() => ({
    id: savedInitialSessionId ?? '',
    messages: [],
    created_at: new Date(),
    updated_at: new Date(),
    message_count: 0,
    interview_mode: true,
  }))
  const [isLoadingMessages, setIsLoadingMessages] = useState(true)
  const [isLoadingMoreMessages, setIsLoadingMoreMessages] = useState(false)
  const [hasMoreMessages, setHasMoreMessages] = useState(false)
  const [isLoadingPreviousMessages, setIsLoadingPreviousMessages] =
    useState(false)
  const [hasMorePreviousMessages, setHasMorePreviousMessages] = useState(false)
  const [isGuestMode, setIsGuestMode] = useState(false)
  const [pagination, setPagination] = useState<PaginationState>({
    startIndex: 0,
    endIndex: 0,
    totalMessages: 0,
  })
  const hasBootstrappedRef = useRef(false)
  const latestSessionIdRef = useRef<string | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)
  const loadMoreAbortControllerRef = useRef<AbortController | null>(null)
  const loadPreviousAbortControllerRef = useRef<AbortController | null>(null)

  /**
   * Persists new chat messages and reconciles local metadata with the backend response.
   * In guest mode, this is a no-op since messages are only stored in localStorage.
   */
  const saveMessages = useCallback(
    async (newMessages: ChatMessage[], sessionId?: string): Promise<void> => {
      // Skip backend persistence in guest mode
      if (isGuestMode) {
        return
      }

      const targetSessionId = sessionId ?? currentSession.id
      if (!targetSessionId) {
        return
      }
      try {
        const { data, error } = await api.api.chat['add-messages'].post({
            messages: newMessages,
            sessionId: targetSessionId,
          })
        if (error) {
          console.error('Failed to save message:', error)
          return
        }
        const metadata = data?.metadata as SessionMetadataResponse
        if (!metadata) {
          return
        }
        setCurrentSession((prev) => {
          if (prev.id !== targetSessionId) {
            return prev
          }
          return {
            ...prev,
            message_count: metadata.message_count,
            updated_at: new Date(metadata.updated_at),
          }
        })
      } catch (error) {
        console.error('Error saving message:', error)
        alert('Failed to save messages to Honcho! sorry :(')
      }
    },
    [currentSession.id, isGuestMode]
  )

  /**
   * Appends freshly streamed messages into the current session view while keeping pagination aligned.
   */
  const renderMessages = useCallback(
    async (messages: ChatMessageWithTimestamp[]): Promise<void> => {
      if (!messages.length) {
        return
      }
      let nextSession: Session | null = null
      setCurrentSession((prevSession) => {
        const mergedMessages = [...prevSession.messages, ...messages]
        const nextCount = Math.max(
          prevSession.message_count,
          mergedMessages.length
        )
        nextSession = {
          ...prevSession,
          messages: mergedMessages,
          message_count: nextCount,
        }
        return nextSession
      })
      setPagination((prev) => ({
        startIndex: prev.startIndex,
        endIndex: prev.endIndex + messages.length,
        totalMessages: prev.totalMessages + messages.length,
      }))
      setHasMoreMessages(false)
    },
    []
  )

  /**
   * Updates the most recent message in place, used while streaming assistant responses.
   */
  const updateCurrentMessage = useCallback(
    async (updatedMessage: ChatMessageWithTimestamp): Promise<void> => {
      let nextSession: Session | null = null
      setCurrentSession((prevSession) => {
        if (!prevSession.messages.length) {
          nextSession = prevSession
          return prevSession
        }
        const nextMessages = [
          ...prevSession.messages.slice(0, -1),
          updatedMessage,
        ]
        nextSession = {
          ...prevSession,
          messages: nextMessages,
          message_count: Math.max(
            prevSession.message_count,
            nextMessages.length
          ),
        }
        return nextSession
      })
    },
    []
  )

  /**
   * Loads available sessions and activates either the saved selection or the most recent session.
   */
  const loadSessions = useCallback(async (): Promise<void> => {
    try {
      const savedId = getSelectedIdentity()
      if (savedId) {
        /**
         * If we have a saved session ID, fetch the context for that session
         * and the available sessions list separately, updating loading variables as each arrives.
         */
        setIsLoadingMessages(true)
        let contextData:
          | Awaited<ReturnType<typeof fetchSessionContext>>
          | undefined
        const fetchContextPromise = fetchSessionContext(savedId).then(
          (result) => {
            contextData = result
            const session = buildSessionFromContext(
              savedId,
              contextData?.context,
              contextData?.metadata
            )
            const { pagination: nextPagination, hasPrevious } =
              derivePaginationFromCounts(
                session.message_count,
                session.messages.length
              )
            setCurrentSession(session)
            setPagination(nextPagination)
            setHasMoreMessages(false)
            setHasMorePreviousMessages(hasPrevious)
            setIsLoadingMessages(false)
            saveSelectedIdentity(savedId)
          }
        )

        await fetchContextPromise
        return
      }
      /**
       * If we don't have a saved session ID, try to fetch the most recent session.
       * If we fail to fetch the most recent session, create a new session.
       */
      setIsLoadingMessages(true)
      const recentResponse = await api.api.chat['get-most-recent-session'].get()
      const { data, error } = recentResponse
      if (!error && data) {
        /**
         * Successfully fetched the most recent session, use the session ID from the response.
         */
        const sessionId = data.sessionId ?? ''
        if (sessionId) {
          const session = buildSessionFromContext(
            sessionId,
            data.context,
            data.metadata
          )
          const { pagination: nextPagination, hasPrevious } =
            derivePaginationFromCounts(
              session.message_count,
              session.messages.length
            )
          setCurrentSession(session)
          setPagination(nextPagination)
          setHasMoreMessages(false)
          setHasMorePreviousMessages(hasPrevious)
          saveSelectedIdentity(sessionId)
        }
        return
      }
      if (error && error.status === 404) {
        /**
         * Failed to fetch the most recent session, create a new session and make it the welcome interview.
         */
        const newSessionId = 'default'
        const newSession = createSession(newSessionId, true)
        setCurrentSession(newSession)
        setPagination({ startIndex: 0, endIndex: 0, totalMessages: 0 })
        setHasMoreMessages(false)
        setHasMorePreviousMessages(false)
        saveSelectedIdentity(newSessionId)
        const assistantMessage: ChatMessageWithTimestamp = {
          role: 'assistant',
          content: '[thinking...]',
          timestamp: new Date(),
        }
        await renderMessages([assistantMessage])
        setIsLoadingMessages(false)
        const { data: stream, error: turnError } = await api.api.chat.turn.post({
              messages: [],
              sessionId: newSessionId,
              interviewMode: true,
            })
        if (turnError || !stream) {
          console.error('Failed to enter interview mode:', turnError)
          return
        }
        if (!(stream instanceof ReadableStream)) {
          console.error('Expected streaming response')
          return
        }
        const reader = stream.getReader()
        const decoder = new TextDecoder()
        let currentMessage = assistantMessage
        while (true) {
          const { done, value } = await reader.read()
          if (done) {
            break
          }
          const text = decoder.decode(value, { stream: true })
          currentMessage = {
            ...currentMessage,
            content: `${currentMessage.content}${text}`,
          }
          await updateCurrentMessage(currentMessage)
        }
        reader.releaseLock()
        await saveMessages([currentMessage], newSessionId)
        return
      }
      console.error('Failed to load most recent session context:', error)
    } catch (error) {
      console.error('Error in loadSessions:', error)
    } finally {
      setIsLoadingMessages(false)
    }
  }, [renderMessages, updateCurrentMessage, saveMessages])

  /**
   * Switches to a different session by ID, loading its context and updating the UI.
   */
  const switchSession = useCallback(async (sessionId: string): Promise<void> => {
    try {
      // Cancel any in-flight requests (session switch and pagination)
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
      if (loadMoreAbortControllerRef.current) {
        loadMoreAbortControllerRef.current.abort()
      }
      if (loadPreviousAbortControllerRef.current) {
        loadPreviousAbortControllerRef.current.abort()
      }

      // Clear all loading states immediately to prevent stuck loaders
      setIsLoadingMoreMessages(false)
      setIsLoadingPreviousMessages(false)

      // Track this as the latest requested session
      latestSessionIdRef.current = sessionId

      // Create new AbortController for this request
      const abortController = new AbortController()
      abortControllerRef.current = abortController

      setIsLoadingMessages(true)

      // Fetch the session context with cancellation support
      const contextData = await fetchSessionContext(sessionId, abortController.signal)

      // Validate that this is still the latest requested session before updating state
      if (latestSessionIdRef.current !== sessionId || abortController.signal.aborted) {
        return
      }

      const session = buildSessionFromContext(
        sessionId,
        contextData?.context,
        contextData?.metadata
      )

      // Update pagination state
      const { pagination: nextPagination, hasPrevious } =
        derivePaginationFromCounts(
          session.message_count,
          session.messages.length
        )

      setCurrentSession(session)
      setPagination(nextPagination)
      setHasMoreMessages(false)
      setHasMorePreviousMessages(hasPrevious)

      // Save the selected session ID
      saveSelectedIdentity(sessionId)
    } catch (error) {
      // Don't log errors for aborted requests
      if (error instanceof Error && error.name !== 'AbortError') {
        console.error('Error switching session:', error)
        // Fallback: create empty session to clear old messages
        // This should rarely happen now that backend always creates sessions
        if (latestSessionIdRef.current === sessionId) {
          const emptySession = createSession(sessionId, false)
          setCurrentSession(emptySession)
          setPagination({ startIndex: 0, endIndex: 0, totalMessages: 0 })
          setHasMoreMessages(false)
          setHasMorePreviousMessages(false)
          saveSelectedIdentity(sessionId)
        }
      }
    } finally {
      // Only clear loading if this is still the latest request
      if (latestSessionIdRef.current === sessionId) {
        setIsLoadingMessages(false)
      }
    }
  }, [])

  /**
   * Fetches the next window of newer messages when the user scrolls forward in history.
   */
  const loadMoreMessages = useCallback(async (): Promise<void> => {
    if (!hasMoreMessages || isLoadingMoreMessages || !currentSession.id || latestSessionIdRef.current !== currentSession.id) {
      return
    }

    // Cancel any previous loadMore request
    if (loadMoreAbortControllerRef.current) {
      loadMoreAbortControllerRef.current.abort()
    }

    const sessionIdAtStart = currentSession.id
    const abortController = new AbortController()
    loadMoreAbortControllerRef.current = abortController

    setIsLoadingMoreMessages(true)
    try {
      const messagesData = await fetchFullSessionMessages(
        currentSession.id,
        undefined,
        pagination.endIndex,
        MESSAGE_PAGE_SIZE,
        abortController.signal
      )

      // Validate we're still on the same session before updating state
      if (latestSessionIdRef.current !== sessionIdAtStart || abortController.signal.aborted) {
        return
      }

      const newMessages = normalizeHonchoMessages(messagesData.messages)
      if (newMessages.length) {
        setCurrentSession((prev) => {
          // Double-check session hasn't changed during state update
          if (prev.id !== sessionIdAtStart) {
            return prev
          }
          return {
            ...prev,
            messages: [...prev.messages, ...newMessages],
          }
        })
      }
      const nextEndIndex =
        messagesData.endIndex ?? pagination.endIndex + newMessages.length
      const nextTotal =
        messagesData.totalMessages ??
        Math.max(pagination.totalMessages, nextEndIndex)
      setPagination((prev) => ({
        startIndex: prev.startIndex,
        endIndex: nextEndIndex,
        totalMessages: nextTotal,
      }))
      setHasMoreMessages(Boolean(messagesData.hasMore))
    } catch (error) {
      // Don't log errors for aborted requests
      if (error instanceof Error && error.name !== 'AbortError') {
        console.error('Error loading more messages:', error)
      }
    } finally {
      // Only clear loading if this is still the same session
      if (latestSessionIdRef.current === sessionIdAtStart) {
        setIsLoadingMoreMessages(false)
      }
    }
  }, [
    currentSession.id,
    hasMoreMessages,
    isLoadingMoreMessages,
    pagination.endIndex,
    pagination.totalMessages,
  ])

  /**
   * Fetches the previous window of older messages when the user scrolls backward in history.
   */
  const loadPreviousMessages = useCallback(async (): Promise<void> => {
    if (
      !hasMorePreviousMessages ||
      isLoadingPreviousMessages ||
      !currentSession.id ||
      latestSessionIdRef.current !== currentSession.id  // Don't load for non-current sessions
    ) {
      return
    }

    // Cancel any previous loadPrevious request
    if (loadPreviousAbortControllerRef.current) {
      loadPreviousAbortControllerRef.current.abort()
    }

    const sessionIdAtStart = currentSession.id
    const abortController = new AbortController()
    loadPreviousAbortControllerRef.current = abortController

    setIsLoadingPreviousMessages(true)
    try {
      const newOffset = Math.max(0, pagination.startIndex - MESSAGE_PAGE_SIZE)
      const limit = pagination.startIndex - newOffset || MESSAGE_PAGE_SIZE
      const messagesData = await fetchFullSessionMessages(
        currentSession.id,
        undefined,
        newOffset,
        limit,
        abortController.signal
      )

      // Validate we're still on the same session before updating state
      if (latestSessionIdRef.current !== sessionIdAtStart || abortController.signal.aborted) {
        return
      }

      const newMessages = normalizeHonchoMessages(messagesData.messages)
      if (newMessages.length) {
        setCurrentSession((prev) => {
          // Double-check session hasn't changed during state update
          if (prev.id !== sessionIdAtStart) {
            return prev
          }
          return {
            ...prev,
            messages: [...newMessages, ...prev.messages],
          }
        })
      }
      const nextStartIndex = messagesData.startIndex ?? newOffset
      const nextTotal =
        messagesData.totalMessages ??
        Math.max(pagination.totalMessages, pagination.endIndex)
      setPagination((prev) => ({
        startIndex: nextStartIndex,
        endIndex: prev.endIndex,
        totalMessages: nextTotal,
      }))
      setHasMorePreviousMessages(nextStartIndex > 0)
    } catch (error) {
      // Don't log errors for aborted requests
      if (error instanceof Error && error.name !== 'AbortError') {
        console.error('Error loading previous messages:', error)
      }
    } finally {
      // Only clear loading if this is still the same session
      if (latestSessionIdRef.current === sessionIdAtStart) {
        setIsLoadingPreviousMessages(false)
      }
    }
  }, [
    currentSession.id,
    hasMorePreviousMessages,
    isLoadingPreviousMessages,
    pagination.endIndex,
    pagination.startIndex,
    pagination.totalMessages,
  ])

  /**
   * Migrates guest session messages to authenticated backend after signup
   */
  const migrateGuestSession = useCallback(
    async (guestMessages: ChatMessage[]): Promise<void> => {
      if (!guestMessages.length) {
        return
      }

      try {
        // Create new session and save all messages in one call
        const newSessionId = 'default'

        // Filter out tool messages - they're internal to conversation flow and not supported by migration API
        const messagesToMigrate = guestMessages.filter(
          (msg) => msg.role !== 'tool'
        ) as Array<{
          role: 'user' | 'assistant' | 'command'
          content: string
        }>

        if (!messagesToMigrate.length) {
          // No messages to migrate after filtering
          return
        }

        const { data: _, error } = await api.api.chat['create-session-with-messages'].post({
            sessionId: newSessionId,
            messages: messagesToMigrate,
          })

        if (error) {
          console.error('Migration API error:', error)
          throw new Error('Failed to migrate guest session')
        }

        // Switch to authenticated mode
        setIsGuestMode(false)

        // Reload authenticated sessions
        await loadSessions()
      } catch (error) {
        console.error('Migration error:', error)
        throw error
      }
    },
    [loadSessions]
  )

  useEffect(() => {
    if (hasBootstrappedRef.current || isAuthLoading) {
      return
    }

    hasBootstrappedRef.current = true

    if (!user) {
      setIsGuestMode(true)
      const guestSession = createGuestSession()

      // Load messages from localStorage if available
      const storedSession = loadGuestSession()
      if (storedSession?.messages && storedSession.messages.length > 0) {
        guestSession.messages = storedSession.messages.map((msg) => ({
          ...msg,
          timestamp: new Date(msg.timestamp),
        }))
        guestSession.message_count =
          storedSession.messageCount ?? storedSession.messages.length
      } else {
        // No stored session - create initial welcome message
        const initialMessage: ChatMessageWithTimestamp = {
          role: 'assistant',
          content: 'may i meet you?',
          timestamp: new Date(),
          id: generateUUID(),
        }

        guestSession.messages = [initialMessage]
        guestSession.message_count = 0

        // Save to localStorage for useGuestSession to pick up
        const newGuestSession: GuestSession = {
          id: 'default',
          messages: [initialMessage],
          messageCount: 0,
          createdAt: new Date().toISOString(),
          isGuestMode: true,
        }
        saveGuestSession(newGuestSession)
      }

      setCurrentSession(guestSession)
      // Always set loading to false for guest mode (no async loading needed)
      setIsLoadingMessages(false)
      return
    }

    void loadSessions()
  }, [isAuthLoading, loadSessions, user])

  // Cleanup: cancel any in-flight requests on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
      if (loadMoreAbortControllerRef.current) {
        loadMoreAbortControllerRef.current.abort()
      }
      if (loadPreviousAbortControllerRef.current) {
        loadPreviousAbortControllerRef.current.abort()
      }
    }
  }, [])
  return {
    currentSession,
    isLoadingMessages,
    hasMoreMessages,
    isLoadingMoreMessages,
    hasMorePreviousMessages,
    isLoadingPreviousMessages,
    isGuestMode,
    loadSessions,
    switchSession,
    saveMessages,
    renderMessages,
    updateCurrentMessage,
    loadMoreMessages,
    loadPreviousMessages,
    migrateGuestSession,
  }
}
